<template>
  <div class="tw-flex tw-flex-col tw-h-full" data-test="contract-details-panel">
    <div v-if="loading || error" tabindex="0">
      <div class="tw-text-right tw-p-3">
        <WdsButtonIcon sm data-test="contract-details-close" @click="emit('close')">
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </div>

      <div v-if="loading" class="tw-mt-[30%] tw-flex tw-justify-center">
        <WdsSpinner :size="40" indeterminate />
      </div>

      <WdsAlert v-else error class="tw-mx-4">
        {{ error }}
        {{ $t('An error occurred') }}
      </WdsAlert>
    </div>

    <WdsStack v-else direction-column justify-space-between class="tw-min-h-screen tw-px-6">
      <!-- Header -->
      <WdsStack direction-row justify-space-between class="tw-h-[72px] tw-pt-5 tw-pb-4">
        <WdsText xl2 primary>{{ $t('Contract details') }}</WdsText>

        <WdsButtonIcon sm data-test="contract-details-close" @click="emit('close')">
          <WdsIcon name="close" :size="16" />
        </WdsButtonIcon>
      </WdsStack>

      <!-- Body -->
      <WdsStack direction-column gap-lg class="tw-flex-1 tw-overflow-y-auto">
        <!-- <ViewContractDetailsCompliances :hire-id="hire?.id" /> -->

        <WdsStack v_if="canViewContract" direction-row justify-end>
          <WdsButtonGhost secondary :href="contract?.pdfUrl" target="_blank">
            <WdsStack align-center gap-sm>
              {{ $t('View full contract') }}
              <WdsIcon :size="16" name="launch" />
            </WdsStack>
          </WdsButtonGhost>
        </WdsStack>

        <WdsStack direction-column gap-lg class="tw-bg-true-gray-50 tw-rounded tw-p-4 tw-w-full">
          <WdsStack direction-column gap-lg>
            <!-- Row: Job title & Time frame -->
            <Row>
              <RowContent :title="$t('Job title')">
                <WdsText secondary>{{ contract?.jobName }}</WdsText>
              </RowContent>

              <RowContent :title="$t('Time frame')">
                <WdsText secondary>{{ timeFrame }}</WdsText>
              </RowContent>
            </Row>

            <!-- Row: PO Number & Rate-->
            <Row>
              <RowContent :title="$t('Purchase order number')">
                <WdsText secondary>{{ contract?.purchaseOrderNumber }}</WdsText>
              </RowContent>

              <RowContent :title="$t('Rate')">
                <WdsStack direction-row>
                  <CurrencyDisplay
                    :converted-currency-code="account.currency"
                    :currency-code="String(contract?.currency)"
                    :rate="Number(contract?.rate)"
                    :show-converted-rates-by-default="false"
                    :show-currency-code="true"
                    disable-convert-currency-modal
                  />
                  <div>
                    &nbsp; / {{ hire?.hasMilestones ? $t('Milestones') : $t(contract?.rateType) }}
                  </div>
                </WdsStack>
              </RowContent>
            </Row>

            <!-- Row: Contract type & Work schedule -->
            <Row>
              <RowContent :title="$t('Contract type')">
                <WdsText secondary>{{ hire?.contractTypeShort }}</WdsText>
              </RowContent>
            </Row>

            <!-- Row: Job location preference & Payment terms -->
            <Row>
              <RowContent :title="$t('Job location preference')">
                <WdsText secondary>{{ jobLocationPreference }}</WdsText>
              </RowContent>

              <RowContent :title="$t('Payment terms')">
                <WdsText secondary>{{ paymentTerms }}</WdsText>
              </RowContent>
            </Row>

            <!-- Row: Job description -->
            <Row>
              <RowContent :title="$t('Job description')">
                <BaseShowMore
                  collapsable
                  :collapse-text="$t('Show less')"
                  :collapsed-height="65"
                  :delay="500"
                  :expand-text="$t('Show more')"
                  align-with-text
                >
                  <WdsText secondary style="white-space: pre-line; overflow: auto">
                    {{ jobDescription }}
                  </WdsText>
                </BaseShowMore>
              </RowContent>
            </Row>
          </WdsStack>

          <WdsDivider align-left>{{ $t('Worker') }}</WdsDivider>

          <Row>
            <RowContent :title="$t('Name')">
              <WdsText secondary>{{ contract?.worker?.name }}</WdsText>
            </RowContent>

            <RowContent :title="$t('Address')">
              <WdsText secondary>{{ contract?.location?.formattedAddress }}</WdsText>
            </RowContent>
          </Row>

          <WdsDivider align-left>{{ $t('Client') }}</WdsDivider>

          <Row>
            <RowContent :title="$t('Name')">
              <WdsText secondary>{{ hire?.company?.name }}</WdsText>
            </RowContent>

            <RowContent :title="$t('Address')">
              <WdsText secondary>{{ hire?.company?.address?.formattedAddress }}</WdsText>
            </RowContent>
          </Row>

          <Row>
            <RowContent :title="$t('CVR')">
              <WdsText secondary>{{ contract?.companyCvr ?? '--' }}</WdsText>
            </RowContent>
          </Row>
        </WdsStack>
      </WdsStack>

      <!-- Footer -->
      <div class="tw-h-[72px] tw-pt-4 tw-pb-4">
        <WdsStack direction-row justify-space-between>
          <WdsButton tertiary md @click="emit('close')">
            {{ $t('Close') }}
          </WdsButton>

          <WdsButton
            v-if="!hideRequestChangesButton"
            :disabled="!hire?.conversation?.viewerCanAccess"
            primary
            md
            :href="`${hire?.conversation?.url}?managecontract=1`"
          >
            {{ $t('Request changes') }}
          </WdsButton>
        </WdsStack>
      </div>
    </WdsStack>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment/moment'
import { computed } from 'vue'
import {
  WdsAlert,
  WdsIcon,
  WdsButtonIcon,
  WdsSpinner,
  WdsText,
  WdsStack,
  WdsButton,
  WdsDivider,
  WdsButtonGhost,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'

import { useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

import BaseShowMore from '@/components/BaseShowMore.vue'
import CurrencyDisplay from '@/components/CurrencyDisplay.vue'
import { ContractFragment, HireFragment } from '@/components/ContractDetailsPanel/graphql/fragments'
import { GetHire } from '@/components/ContractDetailsPanel/graphql/queries'
import Row from '@/components/ContractDetailsPanel/Row.vue'
import RowContent from '@/components/ContractDetailsPanel/RowContent.vue'
import { LocationPreference } from '@/gql/graphql'

const props = defineProps<{
  hireId: string
  forOrganisation?: boolean
  hideRequestChangesButton?: boolean
}>()

const emit = defineEmits(['close', 'updated', 'deleted'])

const account = currentAccount

const { result, loading, error } = useQuery(GetHire, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const contract = computed(() =>
  hire.value !== null && hire.value !== undefined
    ? useFragment(ContractFragment, hire.value.activeContract ?? hire.value.latestContract)
    : null,
)

const timeFrame = computed(() => {
  const startDate = moment(contract.value?.startDate, 'YYYY-MM-DD').isValid()
    ? moment(contract.value?.startDate, 'YYYY-MM-DD').format('MMM D, YYYY')
    : ''

  const endDate = moment(contract.value?.endDate, 'YYYY-MM-DD').isValid()
    ? moment(contract.value?.endDate, 'YYYY-MM-DD').format('MMM D, YYYY')
    : ''

  return !startDate && !endDate ? '--' : `${startDate || '?'} - ${endDate || '?'}`
})

const jobLocationPreference = computed(() => {
  switch (contract.value?.locationPreference) {
    case LocationPreference.OnsiteOnly:
      return $t('On-site')
    case LocationPreference.OnsiteSome:
      return $t('Some onsite')
    case LocationPreference.RemoteOnly:
      return $t('Remote')
    default:
      return $t('No preference')
  }
})

const paymentTerms = computed(() => {
  const paymentTerm = contract.value?.paymentTerm
  const paymentTermMethod = contract.value?.paymentTermMethod

  const isStandardTerms =
    paymentTerm === constantsGlobalVar.payment_term &&
    paymentTermMethod === constantsGlobalVar.payment_term_method

  const textForMethodType =
    paymentTermMethod === 'NET' ? $t('Net (accounting)') : $t('From end of month')

  return `${isStandardTerms ? $t('Standard') : ''} ${paymentTerm} ${$t(
    'days',
  )} ${textForMethodType}`
})

const jobDescription = computed(() => contract.value?.jobDescription ?? '')
</script>

<style lang="scss" scoped>
:deep(.wds-divider-container > .wds-text) {
  @apply tw-bg-true-gray-50;
}
</style>
